import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Package, MapPin, Clock, CheckCircle, XCircle, Truck, 
  CreditCard, Phone, Mail, Calendar, DollarSign, Info, User, X, Store
} from 'lucide-react'
import { api } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { Order, Store as StoreType, StoreStatus } from '../../types'
import ProductImage from '../../components/ProductImage'
import AdminNavHeader from '../../components/AdminNavHeader'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface Employee {
  id: string
  name: string
  email: string
  type: 'chef' | 'delivery' | 'dispatcher' | 'store_admin'
  is_active: boolean
}

const AdminOrderDetails = () => {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [deliveryDrivers, setDeliveryDrivers] = useState<Employee[]>([])
  const [showAssignDeliveryModal, setShowAssignDeliveryModal] = useState(false)
  const [assigningDelivery, setAssigningDelivery] = useState(false)

  useEffect(() => {
    if (orderId) {
      loadOrder()
    }
  }, [orderId])

  const loadOrder = async () => {
    if (!orderId) return

    const cacheKey = `admin_order_details_${orderId}`
    
    // Intentar cargar desde cache primero
    const cachedOrder = getCacheStale<Order>(cacheKey)
    if (cachedOrder) {
      setOrder(cachedOrder)
      setLoading(false)
      
      // Cargar delivery drivers si hay storeId
      if (cachedOrder.storeId) {
        loadDeliveryDrivers(cachedOrder.storeId)
      }
    }

    try {
      setLoading(true)
      const response = await api.getAdminOrder(orderId)
      if (response.success) {
        const orderData = response.data
        // Normalize order data
        const normalizedOrder: Order = {
          id: orderData.id,
          storeId: orderData.store_id || orderData.store?.id,
          customerName: orderData.customer_name,
          customerPhone: orderData.customer_phone,
          customerEmail: orderData.customer_email,
          status: orderData.status,
          totalAmount: Number(orderData.total_amount || 0),
          deliveryFee: Number(orderData.delivery_fee || 0),
          taxAmount: Number(orderData.tax_amount || 0),
          estimatedDeliveryTime: Number(orderData.estimated_delivery_time || 0),
          notes: orderData.notes,
          paymentMethod: orderData.payment_method,
          paymentStatus: orderData.payment_status,
          createdAt: orderData.created_at,
          items: (orderData.items || []).map((item: any) => ({
            id: item.id,
            quantity: Number(item.quantity || 0),
            unitPrice: Number(item.unit_price || 0),
            notes: item.notes,
            variant: item.variant ? {
              id: item.variant.id,
              variant_name: item.variant.variant_name,
              variant_type: item.variant.variant_type,
              stock_quantity: Number(item.variant.stock_quantity) || 0,
              is_available: item.variant.is_available !== false,
              sku: item.variant.sku,
              image_url: item.variant.image_url
            } : undefined,
            product: item.product ? {
              id: item.product.id || item.productId,
              name: item.product.name || 'Producto',
              description: item.product.description,
              price: Number(item.product.price || item.unitPrice) || 0,
              imageUrl: item.product.image_url || item.product.imageUrl,
              categoryId: item.product.category_id || '',
              storeId: orderData.store_id || orderData.store?.id,
              isAvailable: item.product.is_available !== false,
              stockQuantity: Number(item.product.stock_quantity || 999) || 999,
              unit: item.product.unit || 'unidad',
              isFeatured: false
            } : undefined
          })),
          store: orderData.store ? {
            id: orderData.store_id || orderData.store.id,
            name: orderData.store.name || 'Negocio',
            slug: orderData.store.slug || '',
            address: orderData.store.address || '',
            phone: orderData.store.phone || '',
            email: orderData.store.email || '',
            latitude: orderData.store.latitude || 0,
            longitude: orderData.store.longitude || 0,
            status: (orderData.store.status as StoreStatus) || 'pending',
            isActive: orderData.store.is_active !== false,
            description: orderData.store.description || '',
            deliveryFee: orderData.store.delivery_fee || 0,
            minOrderAmount: orderData.store.min_order_amount || 0,
            deliveryTime: orderData.store.delivery_time || 30,
            offersKitchenService: orderData.store.offers_kitchen_service || false,
            offersDelivery: orderData.store.offers_delivery !== false,
            logoUrl: orderData.store.logo_url,
            coverImageUrl: orderData.store.cover_image_url
          } as StoreType : undefined,
          assignedDeliveryId: orderData.assigned_delivery_id || orderData.assignedDeliveryId,
          assignedChefId: orderData.assigned_chef_id || orderData.assignedChefId
        }
        setOrder(normalizedOrder)
        
        // Guardar en cache (2 minutos de TTL - órdenes muy dinámicas)
        setCache(cacheKey, normalizedOrder, 2 * 60 * 1000)
        
        // Load delivery drivers if order has a store
        if (normalizedOrder.storeId) {
          loadDeliveryDrivers(normalizedOrder.storeId)
        }
      }
    } catch (error: any) {
      console.error('Error loading order:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrder) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar el pedido')
    } finally {
      setLoading(false)
    }
  }

  const loadDeliveryDrivers = async (storeId: string) => {
    const cacheKey = `admin_delivery_drivers_${storeId}`
    
    // Intentar cargar desde cache primero
    const cachedDrivers = getCacheStale<Employee[]>(cacheKey)
    if (cachedDrivers && cachedDrivers.length > 0) {
      setDeliveryDrivers(cachedDrivers)
    }

    try {
      const response = await api.get(`/auth/store/${storeId}/employees`)
      if (response.data.success) {
        const employees = response.data.data || []
        const drivers = employees.filter((e: Employee) => e.type === 'delivery' && e.is_active)
        setDeliveryDrivers(drivers)
        
        // Guardar en cache (10 minutos de TTL)
        setCache(cacheKey, drivers, 10 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading delivery drivers:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedDrivers) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-6 h-6 sm:w-7 sm:h-7" />
      case 'preparing': return <Package className="w-6 h-6 sm:w-7 sm:h-7" />
      case 'ready': return <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7" />
      case 'delivering': return <Truck className="w-6 h-6 sm:w-7 sm:h-7" />
      case 'delivered': return <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7" />
      case 'cancelled': return <XCircle className="w-6 h-6 sm:w-7 sm:h-7" />
      default: return <Package className="w-6 h-6 sm:w-7 sm:h-7" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border-2 border-yellow-300'
      case 'preparing':
        return 'bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 border-2 border-blue-300'
      case 'ready':
        return 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-800 border-2 border-purple-300'
      case 'delivering':
        return 'bg-gradient-to-r from-orange-100 to-orange-50 text-orange-800 border-2 border-orange-300'
      case 'delivered':
        return 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border-2 border-green-300'
      case 'cancelled':
        return 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border-2 border-red-300'
      default:
        return 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 border-2 border-gray-300'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente'
      case 'preparing': return 'Preparando'
      case 'ready': return 'Listo'
      case 'delivering': return 'En Camino'
      case 'delivered': return 'Entregado'
      case 'cancelled': return 'Cancelado'
      default: return status
    }
  }

  const getNextStatuses = (currentStatus: string): string[] => {
    // Admin puede revertir estados (ir hacia atrás)
    const isPlatformAdmin = user?.type === 'platform_admin'
    
    switch (currentStatus) {
      case 'pending': 
        return ['preparing', 'cancelled']
      case 'preparing': 
        return ['ready', 'cancelled', ...(isPlatformAdmin ? ['pending'] : [])]
      case 'ready': 
        return ['delivering', 'cancelled', ...(isPlatformAdmin ? ['preparing', 'pending'] : [])]
      case 'delivering': 
        return ['delivered', ...(isPlatformAdmin ? ['ready', 'preparing', 'pending', 'cancelled'] : [])]
      case 'delivered': 
        return isPlatformAdmin ? ['delivering', 'ready', 'preparing', 'pending', 'cancelled'] : []
      case 'cancelled': 
        return isPlatformAdmin ? ['pending', 'preparing', 'ready'] : []
      default: 
        return []
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    if (!order) return

    // Validar que no se pueda cambiar a 'delivering' sin assigned_delivery_id
    if (newStatus === 'delivering' && !order.assignedDeliveryId) {
      toast.error('No se puede marcar como "En Camino" sin asignar un repartidor')
      return
    }

    if (!confirm(`¿Estás seguro de cambiar el estado a "${getStatusText(newStatus)}"?`)) {
      return
    }

    try {
      setUpdatingStatus(true)
      const response = await api.patch(`/orders/${order.id}/status`, { status: newStatus })
      if (response.data.success) {
        toast.success(`Estado actualizado a "${getStatusText(newStatus)}"`)
        loadOrder() // Reload to get updated status
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al actualizar el estado del pedido')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const handleAssignDelivery = async (driverId: string) => {
    if (!order) return

    try {
      setAssigningDelivery(true)
      const response = await api.patch(`/orders/${order.id}/status`, {
        status: order.status, // Keep status as ready, only assign delivery
        assignedDeliveryId: driverId
      })
      
      if (response.data.success) {
        toast.success('Repartidor asignado exitosamente')
        setShowAssignDeliveryModal(false)
        loadOrder() // Reload to get updated assignment
      } else {
        toast.error(response.data.message || 'Error al asignar repartidor')
      }
    } catch (error: any) {
      console.error('Error assigning delivery:', error)
      toast.error(error.response?.data?.message || 'Error al asignar repartidor')
    } finally {
      setAssigningDelivery(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Cargando pedido...</p>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Package className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Pedido no encontrado</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-6">El pedido que buscas no existe o no tienes permisos para verlo.</p>
          <button
            onClick={() => navigate('/admin/orders')}
            className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 active:scale-95 font-medium shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30"
          >
            Volver a Pedidos
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 pb-20 sm:pb-6">
      <AdminNavHeader
        title={`Pedido #${order.id.slice(-8)}`}
        subtitle={getStatusText(order.status)}
      />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6">
        <div className="space-y-3 sm:space-y-4 md:space-y-6">
          {/* Status Card - Mejorado */}
          <div className={`bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg border-2 ${getStatusColor(order.status)}`}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/80 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
              {getStatusIcon(order.status)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs md:text-sm font-semibold text-gray-600 uppercase tracking-wide mb-0.5 sm:mb-1">Estado Actual</p>
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold leading-tight">{getStatusText(order.status)}</p>
            </div>
          </div>
        </div>

          {/* Store Info - Mejorado */}
        {order.store && (
            <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg border-2 border-gray-200/60">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-5 flex items-center gap-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <Store className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                </div>
                <span className="truncate">Información del Negocio</span>
            </h3>
              <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
                <div>
                  <p className="text-sm sm:text-base md:text-lg font-bold text-gray-900">{order.store.name}</p>
                </div>
              {order.store.address && (
                  <div className="flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-200/60">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed">{order.store.address}</p>
                  </div>
              )}
              {order.store.phone && (
                  <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-200/60">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-500 flex-shrink-0" />
                    <p className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">{order.store.phone}</p>
                  </div>
              )}
            </div>
          </div>
        )}

          {/* Customer Info - Mejorado */}
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg border-2 border-gray-200/60">
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-5 flex items-center gap-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <User className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
              </div>
              <span className="truncate">Información del Cliente</span>
          </h3>
            <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
              <div className="p-2.5 sm:p-3 md:p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-blue-200/60">
                <p className="text-[10px] sm:text-xs md:text-sm font-semibold text-blue-700 uppercase tracking-wide mb-0.5 sm:mb-1">Nombre</p>
                <p className="text-sm sm:text-base md:text-lg font-bold text-gray-900">{order.customerName}</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 md:p-4 bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-green-200/60">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs md:text-sm font-semibold text-green-700 uppercase tracking-wide mb-0.5 sm:mb-1">Teléfono</p>
                  <p className="text-xs sm:text-sm md:text-base font-bold text-gray-900 truncate">{order.customerPhone}</p>
                </div>
              </div>
            {order.customerEmail && (
                <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 md:p-4 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-purple-200/60">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-purple-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] sm:text-xs md:text-sm font-semibold text-purple-700 uppercase tracking-wide mb-0.5 sm:mb-1">Correo</p>
                    <p className="text-xs sm:text-sm md:text-base font-bold text-gray-900 truncate">{order.customerEmail}</p>
                  </div>
                </div>
            )}
          </div>
        </div>

          {/* Order Items - Mejorado */}
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg border-2 border-gray-200/60">
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-5 flex items-center gap-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
              </div>
              <span className="truncate">Productos del Pedido</span>
            </h3>
            <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
            {order.items.map((item) => (
                <div key={item.id} className="group flex items-start gap-2.5 sm:gap-3 md:gap-4 p-3 sm:p-4 md:p-5 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border-2 border-gray-200/60 hover:shadow-lg transition-all duration-300">
                {item.product?.imageUrl && (
                    <div className="flex-shrink-0">
                  <ProductImage
                    imageKey={item.product.imageUrl}
                    alt={item.product.name}
                        className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg sm:rounded-xl md:rounded-2xl object-cover border-2 border-gray-200 shadow-md group-hover:scale-105 transition-transform duration-300"
                  />
                    </div>
                )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-1 truncate">{item.product?.name || 'Producto'}</p>
                  {item.variant && (
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 mb-1.5 sm:mb-2">
                        <span className="font-semibold">Variante:</span> {item.variant.variant_name}
                      </p>
                  )}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2">
                      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                        <span className="text-[10px] sm:text-xs md:text-sm text-gray-600">
                          <span className="font-semibold">Cantidad:</span> {item.quantity}
                        </span>
                        <span className="text-[10px] sm:text-xs md:text-sm text-gray-600">
                          <span className="font-semibold">Precio:</span> RD${item.unitPrice.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-sm sm:text-base md:text-lg font-bold text-primary-600">
                    RD${(item.unitPrice * item.quantity).toFixed(2)}
                  </p>
                    </div>
                </div>
              </div>
            ))}
          </div>
        </div>

          {/* Order Summary - Mejorado */}
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg border-2 border-gray-200/60">
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-5 flex items-center gap-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
              </div>
              <span className="truncate">Resumen del Pedido</span>
            </h3>
            <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
              <div className="flex justify-between items-center p-2.5 sm:p-3 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-200/60">
                <span className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Subtotal:</span>
                <span className="text-xs sm:text-sm md:text-base font-bold text-gray-900">RD${(order.totalAmount - order.deliveryFee - order.taxAmount).toFixed(2)}</span>
            </div>
            {order.taxAmount > 0 && (
                <div className="flex justify-between items-center p-2.5 sm:p-3 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-200/60">
                  <span className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Impuestos:</span>
                  <span className="text-xs sm:text-sm md:text-base font-bold text-gray-900">RD${order.taxAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center p-2.5 sm:p-3 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-200/60">
                <span className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Entrega:</span>
                <span className="text-xs sm:text-sm md:text-base font-bold text-gray-900">RD${order.deliveryFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center p-3 sm:p-4 md:p-5 bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border-2 border-primary-200/60 mt-3 sm:mt-4">
                <span className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Total:</span>
                <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary-600">RD${order.totalAmount.toFixed(2)}</span>
            </div>
            </div>
          </div>

          {/* Payment Info - Mejorado */}
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg border-2 border-gray-200/60">
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-5 flex items-center gap-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
        </div>
              <span className="truncate">Información de Pago</span>
          </h3>
            <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
              <div className="flex justify-between items-center p-2.5 sm:p-3 md:p-4 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-200/60">
                <span className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Método:</span>
                <span className="text-xs sm:text-sm md:text-base font-bold text-gray-900">
                {order.paymentMethod === 'cash' ? 'Efectivo' : 
                 order.paymentMethod === 'card' ? 'Tarjeta' : 
                 order.paymentMethod === 'digital_wallet' ? 'Billetera Digital' : 
                 order.paymentMethod || 'N/A'}
              </span>
            </div>
              <div className="flex justify-between items-center p-2.5 sm:p-3 md:p-4 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-200/60">
                <span className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Estado:</span>
                <span className={`text-xs sm:text-sm md:text-base font-bold ${
                order.paymentStatus === 'completed' ? 'text-green-600' :
                order.paymentStatus === 'pending' ? 'text-yellow-600' :
                order.paymentStatus === 'failed' ? 'text-red-600' :
                'text-gray-600'
              }`}>
                {order.paymentStatus === 'completed' ? 'Completado' :
                 order.paymentStatus === 'pending' ? 'Pendiente' :
                 order.paymentStatus === 'failed' ? 'Fallido' :
                 order.paymentStatus || 'N/A'}
              </span>
            </div>
          </div>
        </div>

          {/* Notes - Mejorado */}
        {order.notes && (
            <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg border-2 border-gray-200/60">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-5 flex items-center gap-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <Info className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                </div>
                <span className="truncate">Notas Especiales</span>
            </h3>
              <div className="p-3 sm:p-4 md:p-5 bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border border-amber-200/60">
                <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed">{order.notes}</p>
              </div>
          </div>
        )}

          {/* Actions - Mejorado */}
          <div className="space-y-3 sm:space-y-4 md:space-y-5">
          {/* Assign Delivery Button */}
          {order.status === 'ready' && (
              <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg border-2 border-gray-200/60">
              {order.assignedDeliveryId ? (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 md:p-5 bg-gradient-to-br from-green-50 to-green-100/50 border-2 border-green-200 rounded-lg sm:rounded-xl md:rounded-2xl">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                      <Truck className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm md:text-base font-bold text-green-900 mb-0.5 sm:mb-1">Repartidor Asignado</p>
                      <p className="text-[10px] sm:text-xs md:text-sm text-green-700 truncate">
                      {deliveryDrivers.find(d => d.id === order.assignedDeliveryId)?.name || 'Repartidor asignado'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAssignDeliveryModal(true)}
                      className="w-full sm:w-auto px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-white text-green-700 hover:bg-green-50 rounded-lg sm:rounded-xl md:rounded-2xl text-[10px] sm:text-xs md:text-sm font-bold transition-all duration-200 border-2 border-green-200 shadow-sm hover:shadow-md active:scale-95 min-h-[44px] sm:min-h-0"
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAssignDeliveryModal(true)}
                  disabled={deliveryDrivers.length === 0}
                    className="w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-5 md:px-6 py-3.5 sm:py-4 md:py-4.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg sm:rounded-xl md:rounded-2xl text-xs sm:text-sm md:text-base font-bold transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl min-h-[44px]"
                >
                    <Truck className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                    <span className="hidden sm:inline">{deliveryDrivers.length === 0 ? 'No hay repartidores disponibles' : 'Asignar Repartidor'}</span>
                    <span className="sm:hidden">Asignar</span>
                </button>
              )}
            </div>
          )}

            {/* Status Change Buttons - Mejorado */}
          {getNextStatuses(order.status).length > 0 && (
              <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg border-2 border-gray-200/60">
                <h4 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Cambiar Estado</h4>
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
              {getNextStatuses(order.status).map((nextStatus) => (
                <button
                  key={nextStatus}
                  onClick={() => handleStatusChange(nextStatus)}
                  disabled={updatingStatus}
                      className={`px-3 sm:px-4 md:px-5 lg:px-6 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl md:rounded-2xl text-xs sm:text-sm md:text-base font-bold transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg min-h-[44px] ${
                    nextStatus === 'cancelled'
                          ? 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 hover:from-red-100 hover:to-red-200 border-2 border-red-300'
                          : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 border-2 border-primary-500'
                  }`}
                >
                  {getStatusText(nextStatus)}
                </button>
              ))}
                </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Assign Delivery Modal - Mejorado */}
      {showAssignDeliveryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] flex flex-col">
            <div className="sticky top-0 bg-white/80 backdrop-blur-xl border-b-2 border-gray-200/60 px-4 sm:px-5 md:px-6 py-3 sm:py-4 md:py-5 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Asignar Repartidor</h3>
              <button
                onClick={() => setShowAssignDeliveryModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-colors active:scale-95 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-4 sm:p-5 md:p-6 overflow-y-auto">
              {deliveryDrivers.length === 0 ? (
                <div className="text-center py-6 sm:py-8 md:py-12">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 md:mb-6 shadow-lg">
                    <Truck className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-gray-400" />
                  </div>
                  <p className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-1 sm:mb-2">No hay repartidores disponibles</p>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 px-4">Agrega repartidores en la sección de empleados del negocio</p>
                </div>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {deliveryDrivers.map((driver) => (
                    <button
                      key={driver.id}
                      onClick={() => handleAssignDelivery(driver.id)}
                      disabled={assigningDelivery}
                      className="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-br from-gray-50 to-gray-100/50 hover:from-primary-50 hover:to-primary-100/50 rounded-lg sm:rounded-xl md:rounded-2xl border-2 border-gray-200/60 hover:border-primary-300 transition-all duration-300 disabled:opacity-50 active:scale-95 min-h-[60px] sm:min-h-0"
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <Truck className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-xs sm:text-sm md:text-base font-bold text-gray-900 truncate">{driver.name}</p>
                        <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 truncate">{driver.email}</p>
                      </div>
                      {assigningDelivery && (
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-primary-600 border-t-transparent flex-shrink-0"></div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminOrderDetails
